import { format } from 'date-fns';
import _ from 'lodash';
import React, { useState, useCallback, useEffect, useRef, useContext } from 'react';
import { ClaimResponsibilityType } from '../../../api';
import { CoreFetch } from '../../../api';
import { ClaimApi } from '../../../api/server';
import { MessagingContext } from '../../../services';
import { ResponsibilityBadge } from '../../badge';
import { Spinner } from '../../base';
import * as S from './universal-claims-search-input.styles';

export interface IClaim {
    id?: string;
    fromDos?: string;
    throughDos?: string;
    patientFirst?: string;
    patientLast?: string;
    primaryPayerName?: string;
    claimResponsibilityType?: ClaimResponsibilityType;
}

export interface IUniversalClaimsSearchInputProps {
    disabled?: boolean;
    placeholder?: string;
    onClaimSelected?: (claimId: string) => Promise<void>;
    onBulkClaimsSelected?: (claimIds: string[]) => Promise<void>;
}

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const isValidUUID = (str: string): boolean => UUID_REGEX.test(str);

interface IParseResult {
    validIds: string[];
    invalidLines: string[];
}

const parseMultilineIds = (input: string): IParseResult => {
    const lines = input.split('\n')
        .map(line => line.trim())
        .filter(line => line.length > 0);

    const validIds: string[] = [];
    const invalidLines: string[] = [];

    lines.forEach(line => {
        if (isValidUUID(line)) {
            validIds.push(line);
        } else {
            invalidLines.push(line);
        }
    });

    return { validIds: _.uniq(validIds), invalidLines };
};

export const UniversalClaimsSearchInput: React.FunctionComponent<IUniversalClaimsSearchInputProps> = (
    props: IUniversalClaimsSearchInputProps,
): React.ReactElement => {
    const { disabled, placeholder = 'Search for claims or paste claim IDs', onClaimSelected, onBulkClaimsSelected } = props;
    const context = useContext(MessagingContext);
    
    const [inputValue, setInputValue] = useState<string>('');
    const [searchResults, setSearchResults] = useState<IClaim[]>([]);
    const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
    const [errors, setErrors] = useState<string[]>([]);
    const [isProcessing, setIsProcessing] = useState<boolean>(false);
    const [isSearching, setIsSearching] = useState<boolean>(false);
    const [isSelectingClaim, setIsSelectingClaim] = useState<boolean>(false);
    const [selectedIndex, setSelectedIndex] = useState<number>(-1);
    
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Detect input mode: single-line (search) vs multi-line (bulk)
    const getInputMode = useCallback((): 'search' | 'bulk' => {
        const lineCount = inputValue.split('\n').filter(line => line.trim().length > 0).length;
        return lineCount <= 1 ? 'search' : 'bulk';
    }, [inputValue]);

    // Debounced search for single-line mode
    const performSearch = useCallback(async (term: string) => {
        if (!term || term.trim().length === 0) {
            setSearchResults([]);
            setIsDropdownOpen(false);
            setIsSearching(false);
            return;
        }

        setIsSearching(true);
        setIsDropdownOpen(true); // Show dropdown immediately with loading state
        try {
            const results = await context.onClaimSearch?.(term);
            if (results) {
                setSearchResults(results);
                setSelectedIndex(-1);
            }
        } catch (err) {
            console.error('Search error:', err);
            setSearchResults([]);
        } finally {
            setIsSearching(false);
        }
    }, [context]);

    // Handle input change
    const handleInputChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const value = e.target.value;
        setInputValue(value);
        setErrors([]); // Clear errors when typing

        const mode = value.split('\n').filter(line => line.trim().length > 0).length <= 1 ? 'search' : 'bulk';

        if (mode === 'search') {
            // Debounce search
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
            searchTimeoutRef.current = setTimeout(() => {
                performSearch(value.trim());
            }, 300);
        } else {
            // In bulk mode, close dropdown and clear search state
            setIsDropdownOpen(false);
            setSearchResults([]);
            setIsSearching(false);
        }
    }, [performSearch]);

    // Handle blur - process bulk IDs if in bulk mode
    const handleBlur = useCallback(async () => {
        const mode = getInputMode();
        
        if (mode === 'bulk' && inputValue.trim().length > 0) {
            await processBulkIds();
        }
    }, [inputValue, getInputMode]);

    // Process bulk IDs
    const processBulkIds = useCallback(async () => {
        setIsProcessing(true);
        setErrors([]);

        const { validIds, invalidLines } = parseMultilineIds(inputValue);

        const errorMessages: string[] = [];
        const validClaims: IClaim[] = [];
        const notFoundIds: string[] = [];

        // Validate format errors
        if (invalidLines.length > 0) {
            invalidLines.forEach(line => {
                errorMessages.push(`"${line}" - invalid UUID format`);
            });
        }

        // Fetch claims for valid IDs using bulk API call
        if (validIds.length > 0) {
            try {
                // Determine if this is support or provider app
                const isSupportApp = context.account?.supportGrant === true;
                
                // Create filter string with comma-separated EQ: prefixed IDs
                const filterIds = [validIds.map(id => `EQ:${id}`).join(',')];
                
                // Fetch all claims in a single API call
                const fetchedClaims = isSupportApp
                    ? await CoreFetch(ClaimApi).listSupportClaims({ filterId: filterIds })
                    : await CoreFetch(ClaimApi).listProviderClaims({ filterId: filterIds });
                
                // Create a map of fetched claims by ID for quick lookup
                const fetchedClaimsMap = new Map(
                    fetchedClaims
                        .filter(claim => claim.id) // Filter out claims without IDs
                        .map(claim => [claim.id!, claim])
                );
                
                // Check which IDs were found and which were not
                validIds.forEach(id => {
                    const claim = fetchedClaimsMap.get(id);
                    if (claim) {
                        validClaims.push(claim as IClaim);
                    } else {
                        notFoundIds.push(id);
                    }
                });

                // Add not found errors
                notFoundIds.forEach(id => {
                    errorMessages.push(`"${id}" - claim not found`);
                });

            } catch (err) {
                console.error('Bulk validation error:', err);
                errorMessages.push('Error validating claims. Please try again.');
            }
        }

        setIsProcessing(false);

        // Show errors if any
        if (errorMessages.length > 0) {
            setErrors(errorMessages);
        }

        // Call callback with valid claims if any
        if (validClaims.length > 0 && onBulkClaimsSelected) {
            const validClaimIds = _.compact(validClaims.map(c => c.id));
            await onBulkClaimsSelected(validClaimIds);
            
            // Remove valid IDs from textarea, keep invalid ones
            if (errorMessages.length > 0) {
                const remainingLines = inputValue.split('\n')
                    .map(line => line.trim())
                    .filter(line => {
                        if (line.length === 0) return false;
                        // Keep invalid lines
                        return invalidLines.includes(line) || notFoundIds.includes(line);
                    });
                setInputValue(remainingLines.join('\n'));
            } else {
                // Clear input if all valid
                setInputValue('');
            }
        }
    }, [inputValue, context, onBulkClaimsSelected]);

    // Handle claim selection from dropdown
    const handleClaimSelect = useCallback(async (claim: IClaim) => {
        if (isSelectingClaim) {
            return; // Prevent multiple simultaneous selections
        }
        
        if (claim.id && onClaimSelected) {
            setIsSelectingClaim(true);
            try {
                await onClaimSelected(claim.id);
                setInputValue('');
                setSearchResults([]);
                setIsDropdownOpen(false);
                setIsSearching(false);
                setSelectedIndex(-1);
            } finally {
                setIsSelectingClaim(false);
            }
        }
    }, [onClaimSelected, isSelectingClaim]);

    // Handle keyboard navigation
    const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (getInputMode() !== 'search' || !isDropdownOpen || searchResults.length === 0 || isSearching || isSelectingClaim) {
            return;
        }

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setSelectedIndex(prev => 
                    prev < searchResults.length - 1 ? prev + 1 : prev
                );
                break;
            case 'ArrowUp':
                e.preventDefault();
                setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
                break;
            case 'Enter':
                e.preventDefault();
                if (selectedIndex >= 0 && selectedIndex < searchResults.length) {
                    handleClaimSelect(searchResults[selectedIndex]);
                }
                break;
            case 'Escape':
                e.preventDefault();
                setIsDropdownOpen(false);
                setSelectedIndex(-1);
                break;
        }
    }, [getInputMode, isDropdownOpen, searchResults, selectedIndex, isSearching, isSelectingClaim, handleClaimSelect]);

    // Auto-resize textarea
    useEffect(() => {
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
            const scrollHeight = textareaRef.current.scrollHeight;
            const maxHeight = 10 * 24; // 10 rows * approximate line height
            textareaRef.current.style.height = `${Math.min(scrollHeight, maxHeight)}px`;
        }
    }, [inputValue]);

    // Click outside to close dropdown
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (
                dropdownRef.current &&
                textareaRef.current &&
                !dropdownRef.current.contains(e.target as Node) &&
                !textareaRef.current.contains(e.target as Node)
            ) {
                setIsDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Format date helper
    const formatDate = (d: Date): string => format(d, 'MMM d');
    const parseDate = (d: string | undefined): Date => (d ? new Date(d) : new Date());

    // Render search results
    const renderSearchResults = () => {
        if (!isDropdownOpen) return null;

        if (isSearching) {
            return (
                <S.Dropdown ref={dropdownRef}>
                    <S.LoadingMessage>
                        <Spinner />
                        <span style={{ marginLeft: '8px' }}>Searching...</span>
                    </S.LoadingMessage>
                </S.Dropdown>
            );
        }

        if (isSelectingClaim) {
            return (
                <S.Dropdown ref={dropdownRef}>
                    <S.LoadingMessage>
                        <Spinner />
                        <span style={{ marginLeft: '8px' }}>Loading claim...</span>
                    </S.LoadingMessage>
                </S.Dropdown>
            );
        }

        if (searchResults.length === 0) return null;

        return (
            <S.Dropdown ref={dropdownRef}>
                {searchResults.map((claim, index) => {
                    const from = claim.fromDos ? formatDate(parseDate(claim.fromDos)) : undefined;
                    const through = claim.throughDos ? formatDate(parseDate(claim.throughDos)) : undefined;
                    const serviceDate = through ? `${from} – ${through}` : from;
                    const totalCharges = _.get(claim, 'chargeAmount.amount', Number.NaN);

                    return (
                        <S.SearchResult 
                            key={claim.id} 
                            isSelected={index === selectedIndex}
                            onClick={() => handleClaimSelect(claim)}
                        >
                            <S.SearchResultRow>
                                <S.SearchResultCell>{serviceDate}</S.SearchResultCell>
                                <S.SearchResultCell>
                                    {claim.patientFirst} {claim.patientLast}
                                </S.SearchResultCell>
                            </S.SearchResultRow>
                            <S.SearchResultRow>
                                <S.SearchResultCell>
                                    <ResponsibilityBadge size="small" responsibility={claim.claimResponsibilityType} />
                                </S.SearchResultCell>
                                <S.SearchResultCell>{claim.primaryPayerName}</S.SearchResultCell>
                                <S.SearchResultCell>
                                    ${!Number.isNaN(totalCharges) ? totalCharges : 0}
                                </S.SearchResultCell>
                            </S.SearchResultRow>
                        </S.SearchResult>
                    );
                })}
            </S.Dropdown>
        );
    };

    // Render errors
    const renderErrors = () => {
        if (errors.length === 0) return null;

        return (
            <S.ErrorContainer>
                <S.ErrorTitle>Invalid claim IDs:</S.ErrorTitle>
                <S.ErrorList>
                    {errors.map((error, index) => (
                        <S.ErrorItem key={index}>{error}</S.ErrorItem>
                    ))}
                </S.ErrorList>
            </S.ErrorContainer>
        );
    };

    return (
        <S.Container>
            <S.TextareaWrapper>
                <S.Textarea
                    ref={textareaRef}
                    value={inputValue}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    onKeyDown={handleKeyDown}
                    placeholder={placeholder}
                    disabled={disabled || isProcessing || isSelectingClaim}
                    rows={1}
                />
                {isProcessing && (
                    <S.LoadingOverlay>
                        <Spinner />
                    </S.LoadingOverlay>
                )}
            </S.TextareaWrapper>
            {renderSearchResults()}
            {renderErrors()}
        </S.Container>
    );
};

